// ============================================================================
// STATISTIQUES › VENTES › RÉSUMÉ — l'essentiel d'un coup d'œil : ce qui rentre, ce que ça rapporte, et qui tire le
// résultat. Réf. maquette du back-office (« RÉSUMÉ (univers Ventes) ») et docs/CADRAGE-Statistiques-Ventes.md.
//
// ⚠️ L'ÉCRAN DESSINE, IL NE DÉFINIT AUCUN NOMBRE : totaux, taux, parts, seaux, partage des 100 € et faits viennent du
//    serveur (`sales/stats-summary.ts`). Ce qui se calcule ici est de la GÉOMÉTRIE (positions, tracés, échelles).
// ⚠️ UN INDICATEUR NON MESURABLE S'AFFICHE « — », JAMAIS ZÉRO (marge dont un coût manque, transformation sans compteur).
// ⚠️ « QUAND LE MAGASIN VEND » EST LE CA RÉEL DE CHAQUE HEURE : la maquette le simule par des poids fixes, pas nous.
// ============================================================================
import { useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Icon, rovingRadio, useChartTip } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';
import { currentCurrency, formatMoney, formatMoneyCompact } from '@orgazone/money-format';
import { rescale } from '@orgazone/core-money';
import type { MissingCostsParams, SalesSummaryAxis, SalesSummaryTotals, SalesSummaryView, SummaryObjectiveView } from '../api';
import { key, nombre, t, type Translatable } from '../i18n';
import { moisLongLisible } from '../periode';
import { rangeLabel } from './stats-bar';
import { dTx, eur0, pc, pctOf, shownValue } from './stats-format';
import { MissingCostNotice, missingCostTitle } from './marge-manquante';
import { fieldLabel } from '../vocabulary';
import type { VocabularyFieldKey } from '@orgazone/sync-contract';

// ─── Mises en forme ──────────────────────────────────────────────────────────────────────────────

/** Un nombre d'UNITÉS de la devise (« 11 € », « 100 € ») — les parts de « 100 € d'étiquette » sont des unités, pas des centimes. */
const wholeUnits = (n: number): string => formatMoney(rescale(n, 0, currentCurrency().scale), { displayScale: 0 });

/** `AAAAMMJJ` → `AAAA-MM-JJ`. */
export const isoOfYmd = (ymd: string): string => `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;

/**
 * Le ton d'une évolution : neutre si nulle ou petite, vert si elle va dans le bon sens, rose sinon.
 * ⚠️ `neutral` : un écart qui ne se juge pas — une couverture plus courte n'est ni bien ni mal en soi (Résumé du stock).
 */
export function chip(bp: number | null, inverse = false, neutral = false): { bg: string; fg: string } {
  const flat = neutral || bp === null || Math.abs(bp) < 50;
  const good = bp !== null && (inverse ? bp < 0 : bp > 0);
  return flat ? { bg: 'var(--ink-100)', fg: 'var(--ink-600)' }
    : good ? { bg: 'var(--teal-100)', fg: 'var(--teal-800)' } : { bg: 'var(--rose-bg)', fg: 'var(--rose-fg)' };
}

/** Les teintes de la charte : fond doux, encre, couleur pleine. */
export const TONES = {
  teal: ['var(--teal-100)', 'var(--teal-800)', 'var(--teal-500)'],
  violet: ['var(--violet-bg)', 'var(--violet-fg)', 'var(--violet)'],
  blue: ['var(--blue-bg)', 'var(--blue-fg)', 'var(--blue)'],
  amber: ['var(--amber-bg)', 'var(--amber-fg)', 'var(--amber)'],
  rose: ['var(--rose-bg)', 'var(--rose-fg)', 'var(--rose)'],
} as const;
export type Tone = keyof typeof TONES;

// ─── Géométrie ───────────────────────────────────────────────────────────────────────────────────

/** La petite courbe d'un indicateur, dans une boîte 100 × 30 : le trait et l'aire sous lui. */
export function sparkPaths(vals: (number | null)[]): { line: string; area: string } {
  const v = vals.filter((x): x is number => x !== null && Number.isFinite(x));
  if (v.length < 2) return { line: '', area: '' };
  const mn = Math.min(...v);
  const mx = Math.max(...v);
  const rg = mx - mn || 1;
  const k = vals.length - 1;
  const line = vals.map((x, i) => `${i ? 'L' : 'M'}${((i / k) * 100).toFixed(1)},${(26 - (((x ?? mn) - mn) / rg) * 21).toFixed(1)}`).join('');
  return { line, area: `${line}L100,30L0,30Z` };
}

/** Un haut d'axe « rond » en quatre graduations, sans dépasser ~1,25 × le maximum. */
export function niceTop(max: number): number {
  if (!(max > 0)) return 1;
  const raw = max / 4;
  const p = 10 ** Math.floor(Math.log10(raw));
  return ([1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map((x) => x * p).find((x) => x >= raw) ?? 10 * p) * 4;
}

/** Le libellé compact d'un axe : « 12,4 k€ », « 240 € » (la devise du magasin), « 3,2 k » pour des pièces. */
export function axisLabel(v: number, unit: 'eur' | 'qty'): string {
  if (unit === 'eur') return formatMoneyCompact(Math.round(v));
  const a = Math.abs(v);
  const sg = v < 0 ? '−' : '';
  return sg + (a >= 10000 ? `${nombre(Math.round(a / 1000))} k` : a >= 1000 ? `${nombre(a / 1000, { fractionDigits: 1 })} k` : nombre(Math.round(a)));
}

/** Une courbe lissée SANS dépassement (monotone, Fritsch–Carlson) : une courbe de ventes ne plonge jamais sous zéro. */
export function monotonePath(P: [number, number][]): string {
  const f = (v: number) => Math.round(v * 10) / 10;
  const n = P.length;
  if (!n) return '';
  if (n === 1) return `M0,${f(P[0]![1])}L1000,${f(P[0]![1])}`;
  const m: number[] = [];
  const tg: number[] = [];
  for (let i = 0; i < n - 1; i++) m[i] = (P[i + 1]![1] - P[i]![1]) / (P[i + 1]![0] - P[i]![0]);
  tg[0] = m[0]!;
  tg[n - 1] = m[n - 2]!;
  for (let i = 1; i < n - 1; i++) tg[i] = m[i - 1]! * m[i]! <= 0 ? 0 : (m[i - 1]! + m[i]!) / 2;
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { tg[i] = 0; tg[i + 1] = 0; continue; }
    const a = tg[i]! / m[i]!;
    const b = tg[i + 1]! / m[i]!;
    const s = a * a + b * b;
    if (s > 9) { const k = 3 / Math.sqrt(s); tg[i] = k * a * m[i]!; tg[i + 1] = k * b * m[i]!; }
  }
  let d = `M${f(P[0]![0])},${f(P[0]![1])}`;
  for (let i = 0; i < n - 1; i++) {
    const h = (P[i + 1]![0] - P[i]![0]) / 3;
    d += `C${f(P[i]![0] + h)},${f(P[i]![1] + h * tg[i]!)} ${f(P[i + 1]![0] - h)},${f(P[i + 1]![1] - h * tg[i + 1]!)} ${f(P[i + 1]![0])},${f(P[i + 1]![1])}`;
  }
  return d;
}

// ─── Les briques de la maquette ──────────────────────────────────────────────────────────────────

export const CARD: CSSProperties = {
  background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
export const MONO_LABEL: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)',
};
export const EMPTY: CSSProperties = {
  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180, padding: 20, fontSize: 'var(--t-body)',
  color: 'var(--ink-500)', textAlign: 'center',
};

/**
 * LE CADRE DU RÉSUMÉ — le pictogramme, la teinte, le titre et la place de chaque carte, lus par la carte ET par son
 * gabarit de chargement : écrits deux fois, le gabarit cesserait de tomber à la place de ce qu'il annonce.
 */
const FRAMES = {
  evolution: { icon: 'trendUp', tone: 'teal', title: key('Évolution du chiffre'), flex: '2 1 560px', minWidth: 'min(100%,560px)' },
  objective: { icon: 'target', tone: 'amber', title: key('Objectif de la période'), flex: '1 1 300px', minWidth: 'min(100%,290px)' },
  label100: { icon: 'barChart', tone: 'blue', title: key('Où vont {cent} d’étiquette'), flex: '1 1 460px', minWidth: 'min(100%,440px)' },
  matrix: { icon: 'bubbles', tone: 'violet', title: key('Qui rapporte vraiment'), flex: '1.2 1 480px', minWidth: 'min(100%,440px)' },
  heatmap: { icon: 'clock', tone: 'teal', title: key('Quand le magasin vend'), flex: '1.2 1 480px', minWidth: 'min(100%,440px)' },
  pareto: { icon: 'curve', tone: 'blue', title: key('Combien de références font le chiffre'), flex: '1 1 440px', minWidth: 'min(100%,420px)' },
} as const satisfies Record<string, { icon: IconName; tone: Tone; title: Translatable; flex: string; minWidth: string }>;
type FrameKey = keyof typeof FRAMES;

/** La boîte d'une carte du cadre — sa place dans la rangée. */
const frameBox = (k: FrameKey): CSSProperties => ({ ...CARD, flex: FRAMES[k].flex, minWidth: FRAMES[k].minWidth });

/** L'en-tête d'une carte du cadre, avec sa phrase de lecture. */
function FrameHead({ k, insight, children }: { k: FrameKey; insight: string; children?: ReactNode }) {
  const f = FRAMES[k];
  // Le partage des 100 € nomme la somme dans la devise du magasin — le seul titre qui porte une valeur.
  const title = k === 'label100' ? t(f.title, { cent: wholeUnits(100) }) : t(f.title);
  return <CardHead icon={f.icon} tone={f.tone} title={title} insight={insight}>{children}</CardHead>;
}

/** Les six indicateurs, dans l'ordre de la rangée : leur nom, leur pictogramme, leur teinte. */
const KPI_FRAMES = {
  netTtc: { label: key('CA net TTC'), icon: 'trendUp', tone: 'teal' },
  margin: { label: key('Marge HT'), icon: 'euro', tone: 'violet' },
  basket: { label: key('Panier moyen'), icon: 'basket', tone: 'blue' },
  idv: { label: key('Indice de vente'), icon: 'layers', tone: 'amber' },
  conversion: { label: key('Transformation'), icon: 'filter', tone: 'teal' },
  discount: { label: key('Taux de remise'), icon: 'percent', tone: 'rose' },
} as const satisfies Record<string, { label: Translatable; icon: IconName; tone: Tone }>;
type KpiKey = keyof typeof KPI_FRAMES;
const KPI_ORDER: readonly KpiKey[] = ['netTtc', 'margin', 'basket', 'idv', 'conversion', 'discount'];

/** La rangée des six indicateurs. */
export const KPI_ROW: CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(176px,1fr))', gap: 11, marginBottom: 13 };
/** Une rangée de cartes. */
export const CARD_ROW: CSSProperties = { display: 'flex', gap: 13, flexWrap: 'wrap', alignItems: 'stretch', marginBottom: 13 };

/** L'en-tête d'une carte : son pictogramme teinté, son titre, sa phrase de lecture, et ses réglages. */
export function CardHead({ icon, tone, title, insight, children }: { icon: IconName; tone: Tone; title: string; insight: string; children?: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11, flexWrap: 'wrap', padding: '14px 16px 6px' }}>
      <span style={{
        width: 32, height: 32, borderRadius: 'var(--r-sm)', background: TONES[tone][0], color: TONES[tone][1], display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}><Icon name={icon} size={16} stroke={1.9} /></span>
      <span style={{ flex: 1, minWidth: 180 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{title}</span>
        <span style={{ display: 'block', marginTop: 2, fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.45, textWrap: 'pretty' }}>{insight}</span>
      </span>
      {children}
    </div>
  );
}

/** Un segmenté de la maquette : piste grise, pastille blanche sous le choix. */
export function Seg<K extends string>({ options, value, onChange, label }: { options: { v: K; l: string }[]; value: K; onChange: (v: K) => void; label: string }) {
  return (
    <span role="radiogroup" aria-label={label} style={{
      display: 'inline-flex', padding: 2, gap: 2, borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)', flexShrink: 0,
    }}>
      {options.map((o, i) => {
        const on = o.v === value;
        return (
          <button key={o.v} type="button" role="radio" aria-checked={on} onClick={() => onChange(o.v)}
            {...rovingRadio({ index: i, count: options.length, checked: on, anyChecked: options.some((x) => x.v === value), onPick: (k) => onChange(options[k]!.v) })}
            style={{
              height: 26, padding: '0 10px', border: 'none', borderRadius: 'var(--r-xs)', background: on ? 'var(--paper)' : 'transparent',
              color: on ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: on ? '0 1px 4px rgba(12,14,16,.14)' : 'none',
              fontSize: 'var(--t-note)', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
            }}>{o.l}</button>
        );
      })}
    </span>
  );
}

/**
 * Le rembourrage latéral d'une tuile d'indicateur — sa petite courbe le DÉBORDE pour toucher les deux bords.
 * ⚠️ Un seul nombre pour les deux : écrits séparément, la courbe s'arrêterait à quelques pixels du bord au premier réglage.
 */
export const KPI_PAD_X = 14;

/** La boîte d'une tuile d'indicateur — la vraie et son gabarit. */
export const KPI_TILE: CSSProperties = {
  background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', padding: `13px ${KPI_PAD_X}px 0`, display: 'flex',
  flexDirection: 'column', minWidth: 0, overflow: 'hidden',
};

// ─── Les indicateurs ─────────────────────────────────────────────────────────────────────────────

/** La tête d'une tuile d'indicateur : son pictogramme teinté et son nom. */
export function KpiLabel({ label, icon, tone }: { label: string; icon: IconName; tone: Tone }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
      <span style={{
        width: 24, height: 24, borderRadius: 'var(--r-xs)', background: TONES[tone][0], color: TONES[tone][1], display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}><Icon name={icon} size={13} stroke={2} /></span>
      <span style={{ ...MONO_LABEL, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
    </div>
  );
}

/** Une tuile d'indicateur : pictogramme, valeur, évolution, sous-titre, petite courbe. */
export function KpiCard({ label, icon, tone, value, growth, inverse, neutral, refLabel, sub, series }: {
  label: string; icon: IconName; tone: Tone; value: string; growth: number | null; inverse?: boolean; neutral?: boolean; refLabel: string; sub: string;
  series: (number | null)[];
}) {
  const c = chip(growth, inverse, neutral);
  const sp = sparkPaths(series);
  const { tipProps, tip } = useChartTip();
  return (
    <div style={KPI_TILE}>
      <KpiLabel label={label} icon={icon} tone={tone} />
      <div style={{
        fontSize: 'var(--t-feature)', fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums',
        marginTop: 8, whiteSpace: 'nowrap',
      }}>{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, minWidth: 0 }}>
        <span role="img" {...tipProps({ label, value: dTx(growth), detail: refLabel })} style={{
          display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 7px', borderRadius: 'var(--r-pill)', background: c.bg, color: c.fg,
          fontSize: 'var(--t-meta)', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flexShrink: 0,
        }}>{dTx(growth)}</span>
        <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{sub}</span>
      </div>
      <svg viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden style={{ display: 'block', width: `calc(100% + ${2 * KPI_PAD_X}px)`, height: 34, margin: `8px -${KPI_PAD_X}px 0` }}>
        <path d={sp.area} style={{ fill: `color-mix(in oklch, ${TONES[tone][2]} 16%, transparent)`, stroke: 'none' }} />
        <path d={sp.line} vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: TONES[tone][2], strokeWidth: 1.8, strokeLinejoin: 'round' }} />
      </svg>
      {tip}
    </div>
  );
}

// ─── L'évolution du chiffre ──────────────────────────────────────────────────────────────────────

type Metric = 'ca' | 'mrg' | 'qty';
const METRIC_OF: Record<Metric, (t: SalesSummaryTotals) => number | null> = {
  ca: (x) => x.netTtcCents, mrg: (x) => x.marginHtCents, qty: (x) => x.qtySold,
};
const GRAIN_NOUN: Record<SalesSummaryView['grain'], Translatable> = { day: key('jour'), week: key('semaine'), month: key('mois') };

/** Le libellé d'un seau sur l'axe (« 04/09 », « sept. ») et dans l'infobulle (« ven. 04/09 », « Semaine du 31/08 »). */
export function bucketLabels(k: string, grain: SalesSummaryView['grain'], lang: string): { axis: string; tip: string } {
  if (grain === 'month') {
    const d = new Date(Number(k.slice(0, 4)), Number(k.slice(4, 6)) - 1, 1);
    const short = new Intl.DateTimeFormat(lang, { month: 'short' }).format(d);
    return { axis: short, tip: `${short} ${k.slice(0, 4)}` };
  }
  const dm = `${k.slice(6, 8)}/${k.slice(4, 6)}`;
  if (grain === 'week') return { axis: dm, tip: t('Semaine du {date}', { date: dm }) };
  const d = new Date(Number(k.slice(0, 4)), Number(k.slice(4, 6)) - 1, Number(k.slice(6, 8)));
  return { axis: dm, tip: `${new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(d)} ${dm}` };
}

function Evolution({ view, refName, lang }: { view: SalesSummaryView; refName: string; lang: string }) {
  const [metric, setMetric] = useState<Metric>('ca');
  const [cumulative, setCumulative] = useState(false);
  const { tipProps, tip } = useChartTip();
  const pick = METRIC_OF[metric];
  const unit = metric === 'qty' ? 'qty' : 'eur';
  const run = (vals: (number | null)[]) => { let s = 0; return vals.map((v) => (cumulative ? (s += v ?? 0) : (v ?? 0))); };
  // ⚠️ UNE MARGE INCONNUE N'EST PAS UNE MARGE NULLE : un seul seau dont un coût manque, et la courbe n'a plus de sens —
  //    la tracer à 0 € sous-estimerait le cumul et désignerait un faux meilleur jour, sous une tuile qui dit « — ».
  // ⚠️ MAIS UN JOUR SANS VENTE N'A RIEN D'INCONNU : sa marge vaut 0 €. Le serveur la rend `null` (une marge sur aucune
  //    ligne n'existe pas pour une tuile), donc seul un seau QUI A VENDU peut déclarer la courbe non mesurable — sinon
  //    la période par défaut, dont le jour en cours n'a pas encore de vente au matin, ne se tracerait jamais.
  const unknownIn = (x: SalesSummaryTotals) => x.tickets > 0 && pick(x) === null;
  const unmeasured = view.buckets.some((b) => unknownIn(b.a));
  // ⚠️ LA COMPARAISON SUIT LA MÊME RÈGLE : une marge inconnue de l'an dernier ne se trace pas à 0 € non plus — la
  //    courbe disparaît et la légende le dit.
  const refUnmeasured = view.buckets.some((b) => b.r !== null && unknownIn(b.r));
  const vA = run(view.buckets.map((b) => pick(b.a)));
  const vR = refUnmeasured ? [] : run(view.buckets.filter((b) => b.r !== null).map((b) => pick(b.r!)));
  const n = vA.length;
  const top = niceTop(Math.max(0, ...vA, ...vR));
  const X = (i: number) => ((i + 0.5) / n) * 1000;
  const Y = (v: number) => 1000 - (v / top) * 1000;
  const lineA = n ? monotonePath(vA.map((v, i) => [X(i), Y(v)])) : '';
  const areaA = lineA ? `${lineA}L${n === 1 ? '1000' : X(n - 1).toFixed(1)},1000L${n === 1 ? '0' : X(0).toFixed(1)},1000Z` : '';
  const lineR = vR.length ? monotonePath(vR.map((v, i) => [X(i), Y(v)])) : '';
  let pk = 0;
  vA.forEach((v, i) => { if (v > vA[pk]!) pk = i; });
  const full = (v: number | undefined) => (v === undefined ? '—' : unit === 'qty' ? t('{n} pièce', { n: v }) : eur0(v));
  const growth = metric === 'ca' ? view.growth.netTtc : metric === 'mrg' ? view.growth.margin : null;
  const noun = t(GRAIN_NOUN[view.grain]);
  const labels = view.buckets.map((b) => bucketLabels(b.key, view.grain, lang));
  const insight = n === 0 ? t('Aucun jour ouvré dans la période choisie.') : unmeasured ? t('Non mesurable sur cette période.')
    : (cumulative ? t('Cumul {montant}', { montant: full(vA[n - 1]) }) : t('Meilleur {grain} : {quand} — {montant}', { grain: noun, quand: labels[pk]!.tip, montant: full(vA[pk]) }))
      + (growth === null ? '' : ` · ${t('{evolution} vs {reference}', { evolution: dTx(growth), reference: refName })}`);
  const step = Math.max(1, Math.ceil(n / 7));
  const pkOn = n > 1 && !cumulative;
  return (
    <div style={frameBox('evolution')}>
      <FrameHead k="evolution" insight={insight}>
        <Seg label={t('Indicateur affiché')} value={metric} onChange={setMetric}
          options={[{ v: 'ca', l: t('CA TTC') }, { v: 'mrg', l: t('Marge HT') }, { v: 'qty', l: t('Pièces') }]} />
        <Seg label={t('Mode d’affichage')} value={cumulative ? '1' : '0'} onChange={(v) => setCumulative(v === '1')}
          options={[{ v: '0', l: t('Par {grain}', { grain: noun }) }, { v: '1', l: t('Cumulé') }]} />
      </FrameHead>
      {n > 0 && unmeasured ? (
        <div style={EMPTY}>{t('La marge n’est pas mesurable sur cette période : au moins une vente n’a pas de coût d’achat connu.')}</div>
      ) : n > 0 ? (
        <>
          <div style={{ padding: '8px 16px 0' }}>
            <div style={{ position: 'relative', height: 240, marginLeft: 48 }}>
              {[0, 0.25, 0.5, 0.75, 1].map((f) => (
                <div key={f}>
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: `${f * 100}%`, borderTop: '1px dashed var(--ink-200)' }} />
                  <span style={{
                    position: 'absolute', right: 'calc(100% + 8px)', transform: 'translateY(50%)', fontSize: 'var(--t-sub)', color: 'var(--ink-500)',
                    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', bottom: `${f * 100}%`,
                  }}>{axisLabel(top * f, unit)}</span>
                </div>
              ))}
              <svg viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                <defs>
                  <linearGradient id="statsEvGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" style={{ stopColor: 'var(--teal-500)', stopOpacity: 0.34 }} />
                    <stop offset="1" style={{ stopColor: 'var(--teal-500)', stopOpacity: 0 }} />
                  </linearGradient>
                </defs>
                <path d={areaA} style={{ fill: 'url(#statsEvGrad)', stroke: 'none' }} />
                <path d={lineR} vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: 'var(--blue)', strokeWidth: 2, strokeDasharray: '6 5', strokeLinecap: 'round' }} />
                <path d={lineA} vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: 'var(--teal-600)', strokeWidth: 2.6, strokeLinejoin: 'round', strokeLinecap: 'round' }} />
              </svg>
              {pkOn && (
                <span style={{
                  position: 'absolute', left: `${((pk + 0.5) / n) * 100}%`, bottom: `${(vA[pk]! / top) * 100}%`, width: 12, height: 12, borderRadius: '50%',
                  background: 'var(--paper)', border: '3px solid var(--teal-600)', transform: 'translate(-50%,50%)', boxSizing: 'border-box', pointerEvents: 'none', zIndex: 2,
                }} />
              )}
              <div role="list" style={{ position: 'absolute', inset: 0, display: 'flex', zIndex: 3 }}>
                {view.buckets.map((b, i) => {
                  const r = vR[i];
                  return (
                    <div key={b.key} role="listitem" className="oz-stats-ev-col"
                      {...tipProps({ label: labels[i]!.tip, value: full(vA[i]), detail: r === undefined ? undefined : `${refName} ${full(r)}` })}
                      style={{ flex: 1, minWidth: 0, height: '100%', borderRadius: 4 }} />
                  );
                })}
              </div>
              {tip}
            </div>
            <div style={{ position: 'relative', height: 24, marginLeft: 48 }}>
              {labels.map((l, i) => (i % step === 0 ? (
                <span key={view.buckets[i]!.key} style={{
                  position: 'absolute', top: 7, left: `${((i + 0.5) / n) * 100}%`, transform: 'translateX(-50%)', fontSize: 'var(--t-sub)',
                  color: 'var(--ink-500)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                }}>{l.axis}</span>
              ) : null))}
            </div>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px 18px', flexWrap: 'wrap', padding: '9px 16px 13px', marginTop: 4,
            borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-note)', color: 'var(--ink-600)',
          }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 18, height: 3, borderRadius: 2, background: 'var(--teal-600)' }} />
              <strong style={{ color: 'var(--ink-800)' }}>{t('Période')}</strong>{rangeLabel(isoOfYmd(view.period.from), isoOfYmd(view.period.to))}
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 18, borderTop: '2px dashed var(--blue)' }} />
              <strong style={{ color: 'var(--ink-800)' }}>{view.reference.kind === 'n1' ? t('N-1, même jour de semaine') : refName}</strong>{rangeLabel(isoOfYmd(view.reference.from), isoOfYmd(view.reference.to))}
              {refUnmeasured && <span>— {t('non mesurable : un coût d’achat manque')}</span>}
            </span>
            {pkOn && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', border: '2.5px solid var(--teal-600)', boxSizing: 'border-box' }} />
                {t('Meilleur {grain}', { grain: noun })}
              </span>
            )}
          </div>
        </>
      ) : (
        <div style={EMPTY}>{t('Aucun jour ouvré dans la période choisie — élargissez les dates.')}</div>
      )}
    </div>
  );
}

// ─── L'objectif ──────────────────────────────────────────────────────────────────────────────────

/** Un taux en pourcentage ENTIER (« 54 % ») — ce que la jauge porte en son centre. */
const pct0 = (bp: number): string => `${nombre(Math.round(bp / 100))} %`;

/** La couleur d'une atteinte : vert à 100 % et au-delà, ambre à partir de 90 %, rose en dessous. */
export const attainmentColor = (bp: number | null): string =>
  bp === null ? 'var(--ink-300)' : bp >= 10_000 ? 'var(--teal-500)' : bp >= 9_000 ? 'var(--amber)' : 'var(--rose)';

const RING_TRACK = 2 * Math.PI * 52;
const RING_PROJECTION = 2 * Math.PI * 62;

/** « Saisir les objectifs » — le chemin vers la saisie, posé partout où un objectif manque. */
function EnterObjectives() {
  return (
    <Link to="/objectifs" className="oz-stats-export" style={{
      display: 'inline-flex', alignItems: 'center', height: 34, padding: '0 14px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
      background: 'var(--paper)', color: 'var(--ink-800)', fontSize: 'var(--t-body)', fontWeight: 600, textDecoration: 'none',
    }}>{t('Saisir les objectifs')}</Link>
  );
}

/**
 * L'OBJECTIF DE LA PÉRIODE — la jauge (réalisé ÷ objectif de la période CHOISIE), le reste à faire, le rythme réalisé et
 * le rythme nécessaire, et la projection au rythme actuel.
 * ⚠️ RIEN N'EST CALCULÉ ICI : chaque nombre vient du serveur (`sales/objectives.ts`), la carte le dessine.
 * ⚠️ QUATRE ÉTATS SANS JAUGE, ET CHACUN DIT POURQUOI : aucun objectif, un objectif posé sur une partie de la période (une
 *    jauge comparerait les ventes de toute la période à l'objectif d'une partie), un filtre posé (l'objectif est celui du
 *    magasin entier, il ne se compare pas à une sélection), une lecture en échec (son motif réel — les ventes, elles, sont
 *    lues et restent affichées).
 */
function Objective({ objective }: { objective: SummaryObjectiveView }) {
  const empty = (insight: string, note: string, enter: boolean) => (
    <div style={frameBox('objective')}>
      <FrameHead k="objective" insight={insight} />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, minHeight: 220, padding: 20, textAlign: 'center' }}>
        <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', lineHeight: 1.5, maxWidth: 260 }}>{note}</span>
        {enter && <EnterObjectives />}
      </div>
    </div>
  );
  if (objective.state === 'none') {
    return empty(t('Aucun objectif posé sur cette période'), t('Posez un objectif mensuel : la jauge, le rythme nécessaire et la projection apparaîtront ici.'), true);
  }
  if (objective.state === 'unreadable') {
    return empty(t('Objectif illisible'), t('La lecture des objectifs n’a pas abouti : {motif}.', { motif: objective.reason }), false);
  }
  if (objective.state === 'filtered') {
    return empty(t('L’objectif est celui du magasin entier'), t('Il ne se compare pas à une sélection : retirez la recherche et les filtres pour le lire.'), false);
  }
  if (objective.state === 'partial') {
    const lines = [
      ...(objective.missing.length ? [t('Sans objectif : {mois}.', { mois: objective.missing.map((m) => moisLongLisible(m)).join(', ') })] : []),
      ...(objective.undistributed.length ? [t('Sans aucun jour ouvert où se poser : {mois}.', { mois: objective.undistributed.map((m) => moisLongLisible(m)).join(', ') })] : []),
      ...(objective.overcommitted.length ? [t('Jours fixés au-delà de l’objectif du mois : {mois}.', { mois: objective.overcommitted.map((m) => moisLongLisible(m)).join(', ') })] : []),
    ];
    return empty(t('L’objectif ne couvre pas toute la période'), lines.join(' '), true);
  }
  const o = objective;
  const color = attainmentColor(o.attainmentBp);
  const gap = o.objectiveCents - o.realizedHtCents;
  const projected = o.daysLeft > 0 && o.projectionBp !== null;
  const insight = (o.attainmentBp >= 10_000 ? t('Objectif dépassé') : t('Atteint à {taux}', { taux: pc(o.attainmentBp) }))
    + (o.daysLeft > 0 ? ` · ${t('{n} jour restant', { n: o.daysLeft })}` : o.closed ? ` · ${t('période close')}` : '');
  const needed = o.daysLeft > 0 && o.needPerDayCents !== null;
  return (
    <div style={frameBox('objective')}>
      <FrameHead k="objective" insight={insight} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '6px 16px 16px' }}>
        <div style={{ position: 'relative', width: 170, height: 170 }}>
          <svg viewBox="0 0 132 132" aria-hidden style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
            <circle cx="66" cy="66" r="52" style={{ fill: 'none', stroke: 'var(--ink-100)', strokeWidth: 12 }} />
            <circle cx="66" cy="66" r="62" style={{
              fill: 'none', stroke: color, strokeOpacity: projected ? 0.4 : 0, strokeWidth: 3, strokeLinecap: 'round',
              strokeDasharray: projected ? `${(RING_PROJECTION * Math.min(10_000, o.projectionBp!) / 10_000).toFixed(1)} ${RING_PROJECTION.toFixed(1)}` : `0 ${RING_PROJECTION.toFixed(1)}`,
            }} />
            <circle cx="66" cy="66" r="52" style={{
              fill: 'none', stroke: color, strokeWidth: 12, strokeLinecap: o.attainmentBp > 0 ? 'round' : 'butt',
              strokeDasharray: `${(RING_TRACK * Math.min(10_000, Math.max(0, o.attainmentBp)) / 10_000).toFixed(1)} ${RING_TRACK.toFixed(1)}`,
            }} />
          </svg>
          <span style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <span style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{pct0(o.attainmentBp)}</span>
            <span style={{ fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t('de l’objectif HT')}</span>
          </span>
        </div>
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
          {[
            { l: t('Objectif HT'), v: eur0(o.objectiveCents), fg: 'var(--ink-900)' },
            { l: t('Réalisé HT'), v: eur0(o.realizedHtCents), fg: 'var(--ink-900)' },
            { l: gap > 0 ? t('Reste à faire') : t('Dépassement'), v: eur0(Math.abs(gap)), fg: gap > 0 ? 'var(--amber-fg)' : 'var(--teal-800)' },
          ].map((r) => (
            <div key={r.l} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--ink-100)', fontSize: 'var(--t-body)' }}>
              <span style={{ color: 'var(--ink-600)' }}>{r.l}</span>
              <strong style={{ color: r.fg, fontVariantNumeric: 'tabular-nums' }}>{r.v}</strong>
            </div>
          ))}
        </div>
        <div style={{ width: '100%', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {[
            {
              l: t('Rythme réalisé'), v: o.pacePerDayCents === null ? '—' : t('{montant} / jour', { montant: eur0(o.pacePerDayCents) }),
              fg: o.pacePerDayCents !== null && o.pacePerDayCents >= o.objectivePerDayCents ? 'var(--teal-800)' : 'var(--amber-fg)',
            },
            {
              l: needed ? t('Rythme nécessaire') : t('Rythme de l’objectif'),
              v: t('{montant} / jour', { montant: eur0(needed ? o.needPerDayCents! : o.objectivePerDayCents) }), fg: 'var(--ink-800)',
            },
          ].map((r) => (
            <div key={r.l} style={{ padding: '9px 10px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', minWidth: 0 }}>
              <span style={{ display: 'block', ...MONO_LABEL }}>{r.l}</span>
              <span style={{ display: 'block', marginTop: 3, fontSize: 'var(--t-lead)', fontWeight: 700, color: r.fg, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.v}</span>
            </div>
          ))}
        </div>
        {projected && (
          <span style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-dense)', color: 'var(--ink-700)', lineHeight: 1.4 }}>
            <span style={{ width: 16, height: 0, borderTop: `3px solid ${color}`, opacity: 0.5, flexShrink: 0, borderRadius: 2 }} />
            {t('Au rythme actuel : {taux} de l’objectif en fin de période', { taux: pct0(o.projectionBp!) })}
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Où vont 100 € d'étiquette ───────────────────────────────────────────────────────────────────

/** Les quatre parts des 100 € d'étiquette, dans l'ordre de la barre — la carte et son gabarit les nomment ici. */
const LABEL100_PARTS = { discount: key('Remises'), vat: key('TVA'), purchase: key('Achats'), margin: key('Marge HT') } as const;

/**
 * LES 100 € QUAND LA MARGE NE SE MESURE PAS — les remises et la TVA, vraies, et ce qui reste au magasin HT, hachuré :
 * sa part entre achats et marge attend les coûts manquants. L'encart dit lesquels, et ouvre leur liste.
 * ⚠️ RIEN N'EST INVENTÉ : les trois parts viennent du serveur (`label100Partial`), et la quatrième frontière n'est pas
 *    tracée plutôt que tracée au hasard.
 */
function Label100Partial({ view, p, query }: { view: SalesSummaryView; p: NonNullable<SalesSummaryView['label100Partial']>; query: MissingCostsParams }) {
  const tot = view.totals;
  const hatch = 'repeating-linear-gradient(135deg, var(--ink-100) 0 8px, var(--ink-200) 8px 16px)';
  const { tipProps, tip } = useChartTip();
  const parts = [
    { l: t(LABEL100_PARTS.discount), v: p.discount, bg: TONES.rose[2], d: t('Laissé au client : remises, soldes, promotions'), amount: eur0(tot.grossTtcCents - tot.netTtcCents) },
    { l: t(LABEL100_PARTS.vat), v: p.vat, bg: 'var(--ink-300)', d: t('Collectée pour l’État, elle ne vous appartient pas'), amount: eur0(tot.netTtcCents - tot.netHtCents) },
    { l: t('Achats et marge'), v: p.rest, bg: hatch, d: t('Ce qui reste au magasin HT — son partage entre achats et marge attend les coûts manquants'), amount: eur0(tot.netHtCents) },
  ];
  return (
    <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
      <div>
        <div role="list" style={{ display: 'flex', gap: 2, height: 46, borderRadius: 10, overflow: 'hidden' }}>
          {parts.map((s) => (
            <div key={s.l} role="listitem"
              {...tipProps({ label: s.l, value: t('{sur100} sur {cent} affichés', { sur100: wholeUnits(s.v), cent: wholeUnits(100) }), detail: t('{montant} sur la période', { montant: s.amount }) })}
              style={{ width: `${s.v}%`, minWidth: 3, background: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              <span style={{ fontSize: 'var(--t-title)', fontWeight: 800, color: '#131618', whiteSpace: 'nowrap', opacity: s.v >= 7 ? 1 : 0, fontVariantNumeric: 'tabular-nums' }}>
                {wholeUnits(s.v)}
              </span>
            </div>
          ))}
        </div>
        {tip}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 'var(--t-sub)', color: 'var(--ink-500)' }}>
          <span>{wholeUnits(0)}</span><span>{t('{montant} au prix affiché', { montant: wholeUnits(100) })}</span>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8 }}>
        {parts.map((r) => (
          <div key={r.l} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '10px 11px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', minWidth: 0 }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: r.bg, flexShrink: 0, marginTop: 3 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <strong style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}>{r.l}</strong>
                <strong style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{wholeUnits(r.v)}</strong>
              </span>
              <span style={{ display: 'block', marginTop: 2, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.4 }}>{r.d}</span>
              <span style={{ display: 'block', marginTop: 4, fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-700)', fontVariantNumeric: 'tabular-nums' }}>
                {t('{montant} sur la période', { montant: r.amount })}
              </span>
            </span>
          </div>
        ))}
      </div>
      {view.marginGap && <MissingCostNotice gap={view.marginGap} query={query} />}
    </div>
  );
}

function Label100({ view, query }: { view: SalesSummaryView; query: MissingCostsParams }) {
  const p = view.label100;
  const { tipProps, tip } = useChartTip();
  const tot = view.totals;
  const parts = p ? [
    { l: t(LABEL100_PARTS.discount), v: p.discount, c: TONES.rose[2], d: t('Laissé au client : remises, soldes, promotions'), amount: tot.grossTtcCents - tot.netTtcCents },
    { l: t(LABEL100_PARTS.vat), v: p.vat, c: 'var(--ink-300)', d: t('Collectée pour l’État, elle ne vous appartient pas'), amount: tot.netTtcCents - tot.netHtCents },
    { l: t(LABEL100_PARTS.purchase), v: p.purchase, c: TONES.amber[2], d: t('Ce que les pièces vendues vous ont coûté'), amount: tot.purchaseHtCents ?? 0 },
    { l: t(LABEL100_PARTS.margin), v: p.margin, c: 'var(--teal-500)', d: t('Ce qu’il vous reste pour le loyer, les salaires…'), amount: tot.marginHtCents ?? 0 },
  ] : [];
  const xAt = (k: number) => parts.slice(0, k).reduce((s, x) => s + x.v, 0);
  const partial = !p ? view.label100Partial : null;
  // ⚠️ LE MANQUE NE S'ÉCRIT PLUS EN PHRASE ICI : l'encart dit combien d'articles, pourquoi, et ouvre leur liste.
  const insight = p
    ? t('Sur la période : {affiche} affichés en rayon, {marge} de marge HT au bout.', { affiche: eur0(tot.grossTtcCents), marge: eur0(tot.marginHtCents) })
    : partial
      ? t('Sur la période : {affiche} affichés en rayon — la marge attend des coûts d’achat.', { affiche: eur0(tot.grossTtcCents) })
      : tot.grossTtcCents > 0 ? t('La marge attend des coûts d’achat.') : t('Aucune vente sur la période.');
  return (
    <div style={frameBox('label100')}>
      <FrameHead k="label100" insight={insight} />
      {partial && <Label100Partial view={view} p={partial} query={query} />}
      {!p && !partial && view.marginGap && (
        <div style={{ padding: '14px 16px 16px' }}><MissingCostNotice gap={view.marginGap} query={query} /></div>
      )}
      {p && (
        <div style={{ padding: '14px 16px 16px', display: 'flex', flexDirection: 'column', gap: 16, flex: 1 }}>
          <div style={{ position: 'relative', paddingTop: 62 }}>
            {[
              { l: t('payés par le client (TTC)'), v: 100 - p.discount, x: xAt(1), top: 0 },
              { l: t('restent au magasin (HT)'), v: p.purchase + p.margin, x: xAt(2), top: 30 },
            ].map((b) => (
              <div key={b.top} style={{
                position: 'absolute', left: `${b.x}%`, width: `${100 - b.x}%`, top: b.top, display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 3, pointerEvents: 'none',
              }}>
                <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-600)', whiteSpace: 'nowrap' }}>
                  <strong style={{ color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{wholeUnits(b.v)}</strong> {b.l}
                </span>
                <span style={{ alignSelf: 'stretch', height: 7, border: '1.5px solid var(--ink-300)', borderBottom: 'none', borderRadius: '4px 4px 0 0' }} />
              </div>
            ))}
            <div role="list" style={{ display: 'flex', gap: 2, height: 46, borderRadius: 10, overflow: 'hidden' }}>
              {parts.map((s) => (
                <div key={s.l} role="listitem"
                  {...tipProps({ label: s.l, value: t('{sur100} sur {cent} affichés', { sur100: wholeUnits(s.v), cent: wholeUnits(100) }), detail: t('{montant} sur la période', { montant: eur0(s.amount) }) })}
                  style={{ width: `${s.v}%`, minWidth: 3, background: s.c, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  <span style={{ fontSize: 'var(--t-title)', fontWeight: 800, color: '#131618', whiteSpace: 'nowrap', opacity: s.v >= 7 ? 1 : 0, fontVariantNumeric: 'tabular-nums' }}>
                    {wholeUnits(s.v)}
                  </span>
                </div>
              ))}
            </div>
            {tip}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 'var(--t-sub)', color: 'var(--ink-500)' }}>
              <span>{wholeUnits(0)}</span><span>{t('{montant} au prix affiché', { montant: wholeUnits(100) })}</span>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8 }}>
            {parts.map((r) => (
              <div key={r.l} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '10px 11px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', minWidth: 0 }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, background: r.c, flexShrink: 0, marginTop: 3 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <strong style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}>{r.l}</strong>
                    <strong style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{wholeUnits(r.v)}</strong>
                  </span>
                  <span style={{ display: 'block', marginTop: 2, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', lineHeight: 1.4 }}>{r.d}</span>
                  <span style={{ display: 'block', marginTop: 4, fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-700)', fontVariantNumeric: 'tabular-nums' }}>
                    {t('{montant} sur la période', { montant: eur0(r.amount) })}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Qui rapporte vraiment ───────────────────────────────────────────────────────────────────────

/** Les axes de la ventilation — nommés par le mot du magasin, lu au rendu. */
/** Les axes de la ventilation et le champ qui les nomme — l'écran et l'export les lisent ici. */
export const AXES: { v: SalesSummaryAxis; field: VocabularyFieldKey }[] = [
  { v: 'fam', field: 'family' }, { v: 'sup', field: 'supplier' }, { v: 'marq', field: 'brand' }, { v: 'sect', field: 'sector' },
];

function Matrix({ view, axis, onAxis, query }: { view: SalesSummaryView; axis: SalesSummaryAxis; onAxis: (a: SalesSummaryAxis) => void; query: MissingCostsParams }) {
  const { tipProps, tip } = useChartTip();
  // ⚠️ Une part qui n'existe pas (période au total négatif) ne se place pas : la bulle n'aurait pas d'abscisse.
  const rows = view.axis.rows.filter((r) => r.netTtcCents > 0 && r.marginRateBp !== null && r.shareBp !== null);
  // ⚠️ UNE LIGNE SANS MARGE MESURABLE N'A PAS D'ORDONNÉE : écartée, elle se COMPTE et se dit, sinon une famille entière
  //    disparaîtrait de la matrice sans un mot.
  const unplaced = view.axis.rows.filter((r) => r.netTtcCents > 0 && r.marginRateBp === null).length;
  const nM = rows.length;
  const shares = rows.map((r) => pctOf(r.shareBp!));
  const xMax = Math.max(5, Math.ceil((Math.max(0, ...shares) * 1.15) / 5) * 5);
  // ⚠️ SANS MARGE MOYENNE MESURÉE, PAS DE QUADRANTS : la tracer à 0 % rangeait toutes les lignes au-dessus (« 3
  //    locomotives ») ; la recalculer sur les seules lignes mesurées serait la marge partielle qu'on refuse. Les lignes se
  //    placent à leur vrai taux, la part moyenne du CA reste tracée, et l'écran dit pourquoi le reste attend.
  const avgKnown = view.totals.marginRateBp !== null;
  const avgY = pctOf(view.totals.marginRateBp ?? 0);
  const ys = rows.map((r) => pctOf(r.marginRateBp!)).concat(avgKnown ? [avgY] : []);
  const yLo = Math.max(0, Math.floor((Math.min(...ys) - 4) / 5) * 5);
  const yHi = Math.min(100, Math.ceil((Math.max(...ys) + 4) / 5) * 5);
  const yR = yHi - yLo || 1;
  const sx = nM ? 100 / nM : 0;
  const qMax = Math.max(1, ...rows.map((r) => r.qtySold));
  const QD = { tr: TONES.teal[2], tl: TONES.blue[2], br: TONES.amber[2], bl: TONES.rose[2] };
  const counts = { tr: 0, tl: 0, br: 0, bl: 0 };
  const labelled = new Set(rows.slice().sort((a, b) => b.netTtcCents - a.netTtcCents).slice(0, 6).map((r) => r.label));
  const bubbles = rows.map((r) => {
    const x = pctOf(r.shareBp!);
    const y = pctOf(r.marginRateBp!);
    const q = `${y >= avgY ? 't' : 'b'}${x >= sx ? 'r' : 'l'}` as keyof typeof QD;
    if (avgKnown) counts[q] += 1;
    const s = Math.round(16 + 32 * Math.sqrt(r.qtySold / qMax));
    const px = Math.max(2, Math.min(98, (x / xMax) * 100));
    const py = Math.max(3, Math.min(97, ((y - yLo) / yR) * 100));
    return { r, q, s, px, py };
  }).sort((a, b) => b.s - a.s);
  const syB = Math.max(0, Math.min(100, ((avgY - yLo) / yR) * 100));
  const insight = nM < 2 ? t('Il faut au moins deux lignes pour lire une matrice.')
    : !avgKnown ? t('Taux de marge moyen non mesurable : les lignes se placent, les quadrants attendent les coûts manquants.')
    : t('{locomotives} · {volume} à volume sans marge · taux de marge moyen {taux}', {
      locomotives: t('{n} locomotive', { n: counts.tr }), volume: t('{n} ligne', { n: counts.br }), taux: pc(view.totals.marginRateBp),
    });
  const quadLabel = (pos: CSSProperties, color: string, text: string) => (
    <span style={{
      position: 'absolute', ...pos, fontFamily: 'var(--font-mono)', fontSize: 'var(--t-eyebrow)', letterSpacing: '.06em', textTransform: 'uppercase',
      fontWeight: 700, color, pointerEvents: 'none',
    }}>{text}</span>
  );
  return (
    <div style={frameBox('matrix')}>
      <FrameHead k="matrix" insight={insight}>
        <Seg label={t('Regrouper par')} value={axis} onChange={onAxis} options={AXES.map((a) => ({ v: a.v, l: fieldLabel(a.field) }))} />
      </FrameHead>
      {nM >= 2 ? (
        <div style={{ padding: '10px 18px 14px 16px' }}>
          <div style={{ position: 'relative', height: 270, margin: '12px 0 34px 44px', borderLeft: '1px solid var(--ink-200)', borderBottom: '1px solid var(--ink-200)' }}>
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${Math.min(100, (sx / xMax) * 100)}%`, borderLeft: '1.5px dashed var(--ink-200)' }} />
            {avgKnown && (
              <>
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: `${syB}%`, borderTop: '1.5px dashed var(--ink-200)' }} />
                {quadLabel({ top: 6, right: 8 }, 'var(--teal-800)', t('Locomotives'))}
                {quadLabel({ top: 6, left: 8 }, 'var(--blue-fg)', t('À développer'))}
                {quadLabel({ bottom: 6, right: 8 }, 'var(--amber-fg)', t('Volume, peu de marge'))}
                {quadLabel({ bottom: 6, left: 8 }, 'var(--rose-fg)', t('À questionner'))}
              </>
            )}
            {[{ b: 0, l: `${nombre(yLo)} %` }, ...(avgKnown ? [{ b: syB, l: t('moy. {taux}', { taux: `${nombre(Math.round(avgY))} %` }) }] : []), { b: 100, l: `${nombre(yHi)} %` }].map((y) => (
              <span key={`y${y.b}`} style={{
                position: 'absolute', right: 'calc(100% + 8px)', transform: 'translateY(50%)', fontSize: 'var(--t-sub)', color: 'var(--ink-500)',
                fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', bottom: `${y.b}%`,
              }}>{y.l}</span>
            ))}
            {[{ x: 0, l: '0 %' }, { x: 50, l: `${nombre(xMax / 2)} %` }, { x: 100, l: `${nombre(xMax)} %` }].map((x) => (
              <span key={`x${x.x}`} style={{
                position: 'absolute', top: 'calc(100% + 6px)', left: `${x.x}%`, transform: 'translateX(-50%)', fontSize: 'var(--t-sub)',
                color: 'var(--ink-500)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
              }}>{x.l}</span>
            ))}
            <span style={{ position: 'absolute', left: 0, bottom: 'calc(100% + 4px)', ...MONO_LABEL }}>{t('Taux de marge ↑')}</span>
            <span style={{ position: 'absolute', right: 0, top: 'calc(100% + 20px)', ...MONO_LABEL }}>{t('Part du CA →')}</span>
            {bubbles.map((b, i) => (
              <span key={b.r.label} className="oz-stats-bubble" role="img"
                {...tipProps({
                  label: shownValue(b.r.label),
                  value: t('{part} du CA', { part: pc(b.r.shareBp) }),
                  detail: [t('marge {marge}', { marge: pc(b.r.marginRateBp) }), t('{n} pièce', { n: b.r.qtySold }), eur0(b.r.netTtcCents)].join(' · '),
                })}
                style={{
                  position: 'absolute', left: `${b.px}%`, bottom: `${b.py}%`, width: b.s, height: b.s, borderRadius: '50%',
                  background: `color-mix(in oklch, ${avgKnown ? QD[b.q] : 'var(--ink-300)'} 26%, transparent)`,
                  border: `1.5px solid ${avgKnown ? QD[b.q] : 'var(--ink-300)'}`, transform: 'translate(-50%,50%)',
                  zIndex: 10 + i, boxSizing: 'border-box',
                }} />
            ))}
            {bubbles.filter((b) => labelled.has(b.r.label)).map((b) => (
              <span key={`l-${b.r.label}`} style={{
                position: 'absolute', left: `${b.px}%`, bottom: `calc(${b.py}% + ${b.s / 2 + 3}px)`, transform: 'translateX(-50%)',
                fontSize: 'var(--t-sub)', fontWeight: 700, color: 'var(--ink-800)', whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 60,
                textShadow: '0 0 3px var(--paper),0 0 3px var(--paper)',
              }}>{shownValue(b.r.label).length > 16 ? `${shownValue(b.r.label).slice(0, 15)}…` : shownValue(b.r.label)}</span>
            ))}
            {tip}
          </div>
          <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
            {avgKnown
              ? t('Taille des bulles = pièces vendues · pointillés = moyennes de la période')
              : t('Taille des bulles = pièces vendues · pointillé = part moyenne du CA de la période')}
          </span>
        </div>
      ) : (
        <div style={EMPTY}>{t('Il faut au moins deux lignes avec des ventes pour lire la matrice.')}</div>
      )}
      {unplaced > 0 && view.marginGap && (
        <div style={{ padding: '0 16px 14px', display: 'flex', flexDirection: 'column', gap: 6, fontSize: 'var(--t-dense)', color: 'var(--ink-600)' }}>
          <span>{t('{n} ligne de la ventilation n’y figure pas : sa marge ne se mesure pas.', { n: unplaced })}</span>
          <MissingCostNotice gap={view.marginGap} query={query} compact />
        </div>
      )}
    </div>
  );
}

// ─── Quand le magasin vend ───────────────────────────────────────────────────────────────────────

/** Les jours de semaine dans la langue du compte, 1 = lundi … 7 = dimanche (le 5 janvier 2026 est un lundi). */
export const weekdayName = (w: number, lang: string, style: 'short' | 'long'): string =>
  new Intl.DateTimeFormat(lang, { weekday: style }).format(new Date(2026, 0, 4 + w));

function Heatmap({ view, lang }: { view: SalesSummaryView; lang: string }) {
  const h = view.heatmap;
  const { tipProps, tip } = useChartTip();
  const total = view.totals.netTtcCents;
  const hours = Array.from({ length: h.lastHour - h.firstHour + 1 }, (_, i) => h.firstHour + i);
  const cMax = Math.max(0, ...h.weekdays.flatMap((w) => h.cells[w] ?? []));
  const dMax = Math.max(1, ...h.weekdays.map((w) => h.dayTotals[w] ?? 0));
  let peak = { w: -1, i: 0, v: -1 };
  for (const w of h.weekdays) {
    const c = h.cells[w] ?? [];
    for (let i = 0; i < c.length - 1; i++) { const v = c[i]! + c[i + 1]!; if (v > peak.v) peak = { w, i, v }; }
  }
  const best = h.weekdays.slice().sort((a, b) => (h.dayTotals[b] ?? 0) - (h.dayTotals[a] ?? 0))[0];
  const levels = [10, 24, 42, 64, 88];
  const on = h.weekdays.length > 0 && cMax > 0 && total > 0;
  const share = (v: number) => (total > 0 ? pc(Math.round((v * 10000) / total)) : '—');
  const insight = !on || peak.w < 0 ? t('Aucune vente sur la période.')
    : t('Pic : {jour} {debut}h–{fin}h ({part} du CA) · {meilleur} pèse {partJour} de la semaine', {
      jour: weekdayName(peak.w, lang, 'long'), debut: h.firstHour + peak.i, fin: h.firstHour + peak.i + 2, part: share(peak.v),
      meilleur: weekdayName(best!, lang, 'long'), partJour: share(h.dayTotals[best!] ?? 0),
    });
  return (
    <div style={frameBox('heatmap')}>
      <FrameHead k="heatmap" insight={insight} />
      {on ? (
        <>
          <div style={{ padding: '8px 16px 4px', overflowX: 'auto' }}>
            <div style={{ minWidth: 440, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
                <span style={{ width: 38, flexShrink: 0 }} />
                {hours.map((hh) => (
                  <span key={hh} style={{ flex: 1, minWidth: 0, textAlign: 'center', fontSize: 'var(--t-micro)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{`${hh}h`}</span>
                ))}
                <span style={{ width: 92, flexShrink: 0, textAlign: 'right', ...MONO_LABEL }}>{t('Part')}</span>
              </div>
              {h.weekdays.map((w) => (
                <div key={w} style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  <span style={{ width: 38, flexShrink: 0, fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--ink-700)' }}>{weekdayName(w, lang, 'short')}</span>
                  {(h.cells[w] ?? []).map((v, i) => {
                    const r = cMax > 0 ? v / cMax : 0;
                    const lv = r > 0.85 ? 88 : r > 0.65 ? 64 : r > 0.45 ? 42 : r > 0.25 ? 24 : r > 0 ? 10 : 0;
                    const inPeak = peak.w === w && (i === peak.i || i === peak.i + 1);
                    return (
                      <span key={hours[i]} className="oz-stats-hm-cell" role="img"
                        {...tipProps({
                          label: t('{jour} · {debut}h–{fin}h', { jour: weekdayName(w, lang, 'long'), debut: hours[i]!, fin: hours[i]! + 1 }),
                          value: eur0(v),
                          detail: t('{part} du CA', { part: share(v) }),
                        })}
                        style={{ flex: 1, minWidth: 0, height: 28, borderRadius: 6, background: lv ? `color-mix(in oklch, var(--teal-500) ${lv}%, var(--paper))` : 'var(--ink-50)', boxShadow: inPeak ? 'inset 0 0 0 2px var(--teal-800)' : 'none' }} />
                    );
                  })}
                  <span style={{ width: 92, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 7, paddingLeft: 6, boxSizing: 'border-box' }}>
                    <span style={{ flex: 1, height: 6, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
                      <span style={{ display: 'block', height: '100%', width: `${Math.round(((h.dayTotals[w] ?? 0) / dMax) * 100)}%`, background: 'var(--teal-500)', borderRadius: 'var(--r-pill)' }} />
                    </span>
                    <span style={{ minWidth: 40, textAlign: 'right', fontSize: 'var(--t-meta)', fontWeight: 700, color: 'var(--ink-800)', fontVariantNumeric: 'tabular-nums' }}>{share(h.dayTotals[w] ?? 0)}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
          {tip}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap', padding: '9px 16px 13px', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>
            <span>{t('Moins')}</span>
            {levels.map((lv) => <span key={lv} style={{ width: 18, height: 10, borderRadius: 3, background: `color-mix(in oklch, var(--teal-500) ${lv}%, var(--paper))` }} />)}
            <span>{t('Plus')}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 10 }}>
              <span style={{ width: 14, height: 10, borderRadius: 3, boxShadow: 'inset 0 0 0 2px var(--teal-800)' }} />{t('pic de 2 h')}
            </span>
            <span style={{ marginLeft: 'auto' }}>
              {h.hours ? t('Horaires du magasin {horaires} · CA TTC', { horaires: `${h.hours.open}–${h.hours.close}` }) : t('Horaires du magasin non déclarés · CA TTC')}
            </span>
          </div>
        </>
      ) : (
        <div style={EMPTY}>{t('Aucune vente à répartir sur les horaires du magasin.')}</div>
      )}
    </div>
  );
}

// ─── Combien de références font le chiffre ───────────────────────────────────────────────────────

function Pareto({ view }: { view: SalesSummaryView }) {
  const p = view.pareto;
  const { tipProps, tip } = useChartTip();
  const on = p.references >= 3;
  const x80 = p.references ? (p.count80 / p.references) * 100 : 0;
  const line = [{ rankBp: 0, shareBp: 0 }, ...p.curve].map((c, i) => `${i ? 'L' : 'M'}${pctOf(c.rankBp).toFixed(2)},${(100 - pctOf(c.shareBp)).toFixed(2)}`).join('');
  const topMax = p.top[0]?.netTtcCents ?? 1;
  const insight = !on ? t('Trop peu de références pour lire une concentration.')
    : t('{part} des références font 80 % du chiffre ({n} sur {total})', { part: `${nombre(Math.round(x80))} %`, n: p.count80, total: p.references });
  const yTick = (b: number, l: string, strong = false) => (
    <span key={b} style={{
      position: 'absolute', right: 'calc(100% + 8px)', transform: 'translateY(50%)', fontSize: 'var(--t-sub)', fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap', bottom: `${b}%`, color: strong ? 'var(--blue-fg)' : 'var(--ink-500)', fontWeight: strong ? 700 : undefined,
    }}>{l}</span>
  );
  return (
    <div style={frameBox('pareto')}>
      <FrameHead k="pareto" insight={insight} />
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', padding: '6px 16px 14px', alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          {on ? (
            <div style={{ position: 'relative', height: 186, margin: '12px 6px 30px 36px', borderLeft: '1px solid var(--ink-200)', borderBottom: '1px solid var(--ink-200)' }}>
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: '50%', borderTop: '1px dashed var(--ink-100)' }} />
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: '100%', borderTop: '1px dashed var(--ink-100)' }} />
              {yTick(0, '0 %')}{yTick(50, '50 %')}{yTick(80, '80 %', true)}{yTick(100, '100 %')}
              <svg viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                <defs>
                  <linearGradient id="statsPaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" style={{ stopColor: 'var(--blue)', stopOpacity: 0.3 }} />
                    <stop offset="1" style={{ stopColor: 'var(--blue)', stopOpacity: 0 }} />
                  </linearGradient>
                </defs>
                <path d="M0,100L100,0" vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: 'var(--ink-300)', strokeWidth: 1.2, strokeDasharray: '4 4' }} />
                <path d={`${line}L100,100L0,100Z`} style={{ fill: 'url(#statsPaGrad)', stroke: 'none' }} />
                <path d={line} vectorEffect="non-scaling-stroke" style={{ fill: 'none', stroke: 'var(--blue)', strokeWidth: 2.4, strokeLinejoin: 'round' }} />
              </svg>
              <div style={{ position: 'absolute', left: 0, bottom: '80%', width: `${x80}%`, borderTop: '1.5px dashed var(--blue-fg)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', left: `${x80}%`, bottom: 0, height: '80%', borderLeft: '1.5px dashed var(--blue-fg)', pointerEvents: 'none' }} />
              <span role="img" {...tipProps({ label: t('80 % du CA'), value: t('{n} référence', { n: p.count80 }), detail: t('sur {n} référence', { n: p.references }) })} style={{
                position: 'absolute', left: `${x80}%`, bottom: '80%', width: 14, height: 14, borderRadius: '50%', background: 'var(--paper)',
                border: '3px solid var(--blue-fg)', transform: 'translate(-50%,50%)', boxSizing: 'border-box', zIndex: 2,
              }} />
              <span style={{
                position: 'absolute', left: `${x80}%`, top: 'calc(100% + 6px)', transform: 'translateX(-50%)', fontSize: 'var(--t-sub)', fontWeight: 700,
                color: 'var(--blue-fg)', whiteSpace: 'nowrap',
              }}>{t('{part} des réf.', { part: `${nombre(Math.round(x80))} %` })}</span>
              <span style={{ position: 'absolute', right: 0, top: 'calc(100% + 20px)', ...MONO_LABEL }}>{t('Références, de la meilleure à la moins vendue →')}</span>
              {tip}
            </div>
          ) : (
            <div style={EMPTY}>{t('Trop peu de références pour lire une concentration.')}</div>
          )}
        </div>
        <div style={{ flex: '1 1 210px', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <span style={{ ...MONO_LABEL, margin: '8px 0 4px' }}>{t('Les 5 qui pèsent le plus')}</span>
          {p.top.map((x, i) => (
            <div key={x.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--ink-100)' }}>
              <span style={{
                width: 20, height: 20, borderRadius: 'var(--r-pill)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', fontSize: 'var(--t-sub)',
                fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>{i + 1}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-sub)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.code}</span>
                <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{x.label}</span>
                <span style={{ display: 'block', height: 4, marginTop: 4, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: `${Math.round((x.netTtcCents / topMax) * 100)}%`, background: 'var(--blue)', borderRadius: 'var(--r-pill)' }} />
                </span>
              </span>
              <span style={{ flexShrink: 0, textAlign: 'right' }}>
                <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{eur0(x.netTtcCents)}</span>
                <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{pc(x.shareBp)}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Ce qui mérite un coup d'œil ─────────────────────────────────────────────────────────────────

/** L'en-tête de « Ce qui mérite un coup d'œil » — la carte et son gabarit. */
function SignalsHead() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '13px 16px' }}>
      <span style={{
        width: 32, height: 32, borderRadius: 'var(--r-sm)', background: TONES.amber[0], color: TONES.amber[1], display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}><Icon name="bulb" size={16} stroke={1.9} /></span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Ce qui mérite un coup d’œil')}</span>
        <span style={{ display: 'block', marginTop: 2, fontSize: 'var(--t-dense)', color: 'var(--ink-600)' }}>{t('Des faits, pas des alertes : à vous de juger s’il faut agir.')}</span>
      </span>
    </div>
  );
}

function Signals({ view }: { view: SalesSummaryView }) {
  const s = view.signals;
  const out: { c: string; t: string; d: string }[] = [];
  if (s.discountRateBp !== null && s.discountRateBp > 1000) {
    out.push({ c: TONES.amber[2], t: t('Taux de remise à {taux}', { taux: pc(s.discountRateBp) }), d: t('Plus d’un dixième du prix affiché part en remises.') });
  }
  if (s.worstMargin) {
    out.push({ c: TONES.rose[2], t: t('{ligne} — marge {taux}', { ligne: shownValue(s.worstMargin.label), taux: pc(s.worstMargin.marginRateBp) }), d: t('La ligne la moins rentable de la ventilation affichée.') });
  }
  if (s.bestGrowth) {
    out.push({ c: TONES.teal[2], t: t('{ligne} progresse le plus', { ligne: shownValue(s.bestGrowth.label) }), d: t('Plus forte hausse de CA net d’une période à l’autre.') });
  }
  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
      <SignalsHead />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))' }}>
        {out.map((g) => (
          <div key={g.t} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 16px', borderTop: '1px solid var(--ink-100)' }}>
            <span style={{ width: 9, height: 9, borderRadius: 'var(--r-pill)', background: g.c, flexShrink: 0, marginTop: 4 }} />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', lineHeight: 1.35 }}>{g.t}</span>
              <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.45 }}>{g.d}</span>
            </span>
          </div>
        ))}
        {!out.length && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--ink-100)', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
            {t('Rien de saillant sur la période affichée.')}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── L'onglet ────────────────────────────────────────────────────────────────────────────────────

/** Le Résumé d'une période déjà lue : indicateurs, évolution, objectif, partage, matrice, heures, concentration, faits. */
export function StatsResume({ view, axis, onAxis, lang, query }: {
  view: SalesSummaryView; axis: SalesSummaryAxis; onAxis: (a: SalesSummaryAxis) => void; lang: string;
  /** La période et les filtres de la lecture — ceux de la liste des articles sans coût. */
  query: MissingCostsParams;
}) {
  const refName = view.reference.kind === 'n1' ? t('N-1') : t('Comparaison');
  const refShort = view.reference.kind === 'n1' ? t('N-1 {montant}', { montant: eur0(view.refTotals.netTtcCents) }) : t('vs comp. {montant}', { montant: eur0(view.refTotals.netTtcCents) });
  const a = view.totals;
  const r = view.refTotals;
  const series = (f: (x: SalesSummaryTotals) => number | null) => view.buckets.map((b) => f(b.a));
  const tip = (v: string) => (view.reference.kind === 'n1' ? t('N-1 : {valeur}', { valeur: v }) : t('Comparaison : {valeur}', { valeur: v }));
  // ⚠️ PAR NOM ET NON PAR RANG : `Record<KpiKey, …>` réclame chaque indicateur du cadre, et son nom vient du cadre.
  const kpis: Record<KpiKey, { value: string; growth: number | null; inverse?: boolean; sub: string; ref: string; s: (number | null)[] }> = {
    netTtc: { value: eur0(a.netTtcCents), growth: view.growth.netTtc, sub: refShort, ref: tip(eur0(r.netTtcCents)), s: series((x) => x.netTtcCents) },
    // ⚠️ UNE MARGE NON MESURABLE DIT POURQUOI sous sa valeur : « — » seul ne dit pas ce qu'il faut compléter.
    margin: {
      value: eur0(a.marginHtCents), growth: view.growth.margin,
      sub: a.marginHtCents === null && view.marginGap ? missingCostTitle(view.marginGap) : t('taux de marge {taux}', { taux: pc(a.marginRateBp) }),
      ref: tip(eur0(r.marginHtCents)), s: series((x) => x.marginHtCents),
    },
    basket: { value: eur0(a.basketCents), growth: view.growth.basket, sub: t('{n} ticket', { n: a.tickets }), ref: tip(eur0(r.basketCents)), s: series((x) => x.basketCents) },
    idv: { value: a.idvX100 === null ? '—' : nombre(a.idvX100 / 100, { fractionDigits: 2 }), growth: view.growth.idv, sub: t('articles par ticket'), ref: tip(r.idvX100 === null ? '—' : nombre(r.idvX100 / 100, { fractionDigits: 2 })), s: series((x) => x.idvX100) },
    conversion: { value: pc(a.conversionBp), growth: view.growth.conversion, sub: a.passages === null ? t('aucun compteur de passages') : t('{n} passage', { n: a.passages }), ref: tip(pc(r.conversionBp)), s: series((x) => x.conversionBp) },
    discount: { value: pc(a.discountRateBp), growth: view.growth.discount, inverse: true, sub: t('{montant} accordés', { montant: eur0(a.grossTtcCents - a.netTtcCents) }), ref: tip(pc(r.discountRateBp)), s: series((x) => x.discountRateBp) },
  };

  return (
    <>
      <div style={KPI_ROW}>
        {KPI_ORDER.map((k) => {
          const f = KPI_FRAMES[k];
          const v = kpis[k];
          return (
            <KpiCard key={k} label={t(f.label)} icon={f.icon} tone={f.tone} value={v.value} growth={v.growth} inverse={v.inverse}
              refLabel={v.ref} sub={v.sub} series={v.s} />
          );
        })}
      </div>
      <div style={CARD_ROW}>
        <Evolution view={view} refName={refName} lang={lang} />
        <Objective objective={view.objective} />
      </div>
      <div style={CARD_ROW}>
        <Label100 view={view} query={query} />
        <Matrix view={view} axis={axis} onAxis={onAxis} query={query} />
      </div>
      <div style={CARD_ROW}>
        <Heatmap view={view} lang={lang} />
        <Pareto view={view} />
      </div>
      <Signals view={view} />
    </>
  );
}

// ─── Le gabarit de la première mesure ────────────────────────────────────────────────────────────

/**
 * Une forme de matière animée, là où un chiffre ou une courbe va arriver — grise, ou dans la teinte de ce qu'elle annonce.
 * ⚠️ La teinte passe par le style : la feuille ne connaît que le gris, et une teinte par indicateur y ferait cinq règles.
 */
export function Shimmer({ w, h, r = 'var(--r-xs)', tone, style }: { w: number | string; h: number; r?: string; tone?: Tone; style?: CSSProperties }) {
  const tint = tone ? {
    background: `linear-gradient(90deg, ${TONES[tone][0]} 30%, color-mix(in oklch, ${TONES[tone][0]} 35%, var(--paper)) 50%, ${TONES[tone][0]} 70%)`,
    backgroundSize: '200% 100%',
  } : null;
  return <span aria-hidden className="oz-shimmer" style={{ display: 'block', width: w, height: h, borderRadius: r, flexShrink: 0, ...tint, ...style }} />;
}

/** Le gabarit d'une tuile d'indicateur : son vrai nom, et la forme de sa valeur, de son écart et de sa petite courbe. */
function KpiPlaceholder({ k }: { k: KpiKey }) {
  const f = KPI_FRAMES[k];
  return (
    <div style={KPI_TILE}>
      <KpiLabel label={t(f.label)} icon={f.icon} tone={f.tone} />
      <Shimmer w="62%" h={26} style={{ marginTop: 10 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8 }}>
        <Shimmer w={46} h={18} r="var(--r-pill)" />
        <Shimmer w="38%" h={10} />
      </div>
      <Shimmer w={`calc(100% + ${2 * KPI_PAD_X}px)`} h={34} r="0" tone={f.tone} style={{ margin: `9px -${KPI_PAD_X}px 0` }} />
    </div>
  );
}

/** Le gabarit d'une carte : son vrai en-tête, qui dit la mesure en cours, et la forme de son contenu. */
function CardPlaceholder({ k, children }: { k: FrameKey; children: ReactNode }) {
  return (
    <div style={frameBox(k)}>
      <FrameHead k={k} insight={t('Mesure en cours…')} />
      <div style={{ padding: '10px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>{children}</div>
    </div>
  );
}

/**
 * LE RÉSUMÉ PENDANT SA PREMIÈRE MESURE — le cadre réel (les six indicateurs, les cartes, leurs titres et pictogrammes),
 * une matière animée à la place des chiffres et des courbes.
 * ⚠️ UNE EXCEPTION ASSUMÉE À « AUCUN SQUELETTE » (charte, `DataTable`) : un tableau ignore combien de lignes il aura, et
 *    un squelette en inventerait le nombre. Le Résumé a un cadre FIXE, dont les titres sont vrais avant toute donnée ; le
 *    gabarit ne montre aucun chiffre, aucune courbe dessinée, aucun avancement (§23.0). Là où le nombre d'éléments dépend
 *    des données (les heures d'ouverture, les références), il pose UNE forme, jamais des cellules.
 * ⚠️ LE CHARGEMENT SE DIT : chaque carte annonce « Mesure en cours… », et l'ensemble est `aria-busy`.
 * ⚠️ L'objectif aussi a son gabarit : il vient avec les ventes, dans la même réponse.
 */
export function StatsResumePlaceholder() {
  return (
    <div aria-busy="true">
      <div style={KPI_ROW}>
        {KPI_ORDER.map((k) => <KpiPlaceholder key={k} k={k} />)}
      </div>
      <div style={CARD_ROW}>
        <CardPlaceholder k="evolution">
          <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>{t('Les indicateurs sont calculés sur les pièces fiscales du magasin.')}</span>
          {/* Les cinq graduations du vrai graphique — une structure fixe, pas une donnée — sur la matière en vol. */}
          <div style={{ position: 'relative', height: 240 }}>
            <Shimmer w="100%" h={240} r="var(--r-sm)" style={{ opacity: 0.55 }} />
            {[0, 0.25, 0.5, 0.75, 1].map((p) => (
              <span key={p} aria-hidden style={{ position: 'absolute', left: 0, right: 0, bottom: `${p * 100}%`, borderTop: '1px dashed var(--ink-200)' }} />
            ))}
          </div>
        </CardPlaceholder>
        <CardPlaceholder k="objective">
          <Shimmer w={170} h={170} r="50%" style={{ alignSelf: 'center' }} />
          <Shimmer w="100%" h={10} />
          <Shimmer w="70%" h={10} />
        </CardPlaceholder>
      </div>
      <div style={CARD_ROW}>
        <CardPlaceholder k="label100">
          <Shimmer w="100%" h={46} r="10px" style={{ marginTop: 52 }} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 8 }}>
            {Object.entries(LABEL100_PARTS).map(([part, l]) => (
              <div key={part} style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '10px 11px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)' }}>
                <strong style={{ fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}>{t(l)}</strong>
                <Shimmer w="55%" h={10} />
              </div>
            ))}
          </div>
        </CardPlaceholder>
        <CardPlaceholder k="matrix"><Shimmer w="100%" h={300} r="var(--r-sm)" /></CardPlaceholder>
      </div>
      <div style={CARD_ROW}>
        <CardPlaceholder k="heatmap"><Shimmer w="100%" h={220} r="var(--r-sm)" /></CardPlaceholder>
        <CardPlaceholder k="pareto"><Shimmer w="100%" h={220} r="var(--r-sm)" /></CardPlaceholder>
      </div>
      <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
        <SignalsHead />
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--ink-100)' }}><Shimmer w="45%" h={12} /></div>
      </div>
    </div>
  );
}
